import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/**
 * Invite Acceptance History, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/InviteAttemptsPage.tsx` (itself mirroring `apps/admin/app/invite-attempts.tsx`
 * / `specs/admin/screens/invite-attempts.md`): the full audit trail of every attempt to redeem an invite
 * code, success AND failure — distinct from `AdminInvite.usedCount` (successes only). The `useQuery` here
 * is lifted UNCHANGED — only the returned JSX differs.
 */
export function InviteAttemptsPage() {
  const { client, token } = useAuth();

  const attemptsQuery = useQuery({
    queryKey: ["admin-invite-attempts"],
    queryFn: () => client!.adminInviteAttempts(token!),
    enabled: !!client && !!token,
  });
  const attempts = attemptsQuery.data ?? [];

  return (
    <MainCard title="Invite acceptance history">
      {attemptsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(attemptsQuery.error as Error).message}
        </Alert>
      ) : null}
      {attemptsQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : attempts.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          No invite attempts recorded yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attempts.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.email ?? <Typography color="text.secondary">(no email)</Typography>}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{a.code}</TableCell>
                  <TableCell>{new Date(a.at * 1000).toLocaleString()}</TableCell>
                  <TableCell>
                    {a.outcome === "accepted" ? (
                      <Chip label="Accepted" color="success" size="small" />
                    ) : (
                      <Chip label={`Invalid — ${a.reason}`} color="error" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </MainCard>
  );
}
